import { useEffect, useRef } from "react";
import type { Session } from "@supabase/supabase-js";
import { DEFAULT_ACCENT } from "../lib/constants";
import { accentFromUser, applyAccent } from "../lib/theme";
import { supabase } from "../lib/supabaseClient";

// Keeps the page's accent in step with whoever is signed in. `session` is
// useAuth's: undefined while loading (leave the early-applied accent alone),
// null when signed out (back to the default, so the next person on a shared
// device doesn't inherit it).
//
// Runs only when the signed-in person changes, not on every token refresh,
// which would undo a choice made in this tab before the session caught up.
// The stored session can hold user_metadata from before a change made on
// another device, so once signed in this also asks Supabase for the current
// user and applies that.
export function useAccentSync(session: Session | null | undefined): void {
  const who = session === undefined ? "loading" : session === null ? "signed-out" : session.user.id;
  const sessionRef = useRef(session);
  sessionRef.current = session;

  useEffect(() => {
    if (who === "loading") return;
    if (who === "signed-out") {
      applyAccent(DEFAULT_ACCENT);
      return;
    }
    applyAccent(accentFromUser(sessionRef.current?.user));
    let cancelled = false;
    supabase.auth.getUser().then(({ data, error }) => {
      if (!cancelled && !error && data.user) applyAccent(accentFromUser(data.user));
    });
    return () => {
      cancelled = true;
    };
  }, [who]);
}
